import { useEffect, useRef, useState } from 'react';
import { useRecordingContext } from '@context/RecordingContext';
import { FiberManualRecord as RecordIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress, Stack, Typography, useTheme } from '@mui/material';

import { useTelehealthCallManager } from '../context/useTelehealthCallManager';

import { TelehealthSessionInviteControls } from './TelehealthSessionInviteControls';

interface TelehealthActiveSessionCardProps {
  onCopyLink?: () => void;
  onSendLink?: () => void;
  isCopyingLink?: boolean;
  isSendingLink?: boolean;
  linkCopied?: boolean;
  showInviteControls?: boolean;
  subjectLabel?: string;
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function TelehealthActiveSessionCard({
  onCopyLink,
  onSendLink,
  isCopyingLink = false,
  isSendingLink = false,
  linkCopied = false,
  showInviteControls = true,
  subjectLabel,
}: TelehealthActiveSessionCardProps) {
  const theme = useTheme();
  const { session, phase } = useTelehealthCallManager();
  const { recordingStatus, initiateRecording, getRecordingTimes } = useRecordingContext();

  // Delay showing "Start Recording" fallback so it doesn't flash during normal auto-start
  const [showFallback, setShowFallback] = useState(false);
  useEffect(() => {
    if (recordingStatus.isRecording || recordingStatus.isStarting || phase !== 'call') {
      setShowFallback(false);
      return;
    }
    const timer = setTimeout(() => setShowFallback(true), 3000);
    return () => clearTimeout(timer);
  }, [recordingStatus.isRecording, recordingStatus.isStarting, phase]);

  // Read authoritative elapsed time from RecordingContext every second
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!recordingStatus.isRecording) {
      setElapsed(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    const tick = () => {
      const times = getRecordingTimes();
      if (times) setElapsed(times.elapsedTime);
    };
    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [recordingStatus.isRecording, getRecordingTimes]);

  const handleStartRecording = async () => {
    try {
      await initiateRecording();
    } catch {
      // Error handled by RecordingContext
    }
  };

  return (
    <Stack spacing={1.5}>
      {/* Recording status — styled to match RecordingStatusIndicator */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          minHeight: 56,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
          border: '1px solid',
          borderColor: theme.palette.divider,
          width: '100%',
        }}
      >
        {recordingStatus.isRecording ? (
          <>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: recordingStatus.recordingPaused ? 'warning.main' : 'error.main',
                flexShrink: 0,
                ...(!recordingStatus.recordingPaused && {
                  animation: 'cardPulse 2s ease-in-out infinite',
                  '@keyframes cardPulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.4 },
                  },
                }),
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
              {recordingStatus.recordingPaused ? 'Recording Paused' : 'Recording'}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontFamily: 'monospace', fontWeight: 600 }}
            >
              {formatDuration(elapsed)}
            </Typography>
          </>
        ) : (
          <>
            {recordingStatus.isStarting && <CircularProgress size={12} />}
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
              {recordingStatus.isStarting ? 'Starting recording...' : 'Telehealth session active'}
            </Typography>
          </>
        )}
      </Box>

      {/* Fallback: Start Recording button — only after 3s delay */}
      {showFallback && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<RecordIcon />}
          onClick={handleStartRecording}
          fullWidth
          sx={{ textTransform: 'none' }}
        >
          Start Recording
        </Button>
      )}

      {/* Invite controls use the provider's waiting-room link. Patient actions live in CallPanel. */}
      {session?.isOwner && (
        <TelehealthSessionInviteControls
          onCopyLink={showInviteControls ? onCopyLink : undefined}
          onSendLink={showInviteControls ? onSendLink : undefined}
          isCopyingLink={isCopyingLink}
          isSendingLink={isSendingLink}
          linkCopied={linkCopied}
          subjectLabel={subjectLabel}
        />
      )}
    </Stack>
  );
}
